import type { Simulation } from './simulation.ts';
import type { SimEvent, Team } from './types.ts';

/**
 * Visión de equipo (niebla de guerra). La usan el cliente (oscurecer el suelo y
 * ocultar enemigos) y el servidor (cada equipo sólo recibe lo que ve: ahorra
 * ancho de banda e impide quitar la niebla en el cliente).
 */

/** Radio de visión de cada tipo de unidad aliada. */
export const VISION_RADIUS = {
  hero: 20,
  minion: 13,
  tower: 18,
  nexus: 24,
} as const;

export interface VisionSource {
  x: number;
  z: number;
  radius: number;
}

/** Focos de visión del equipo: héroes, minions y estructuras vivos. */
export function visionSources(sim: Simulation, team: Team): VisionSource[] {
  const sources: VisionSource[] = [];
  for (const hero of sim.heroes) {
    if (hero.alive && hero.team === team) sources.push({ x: hero.pos.x, z: hero.pos.z, radius: VISION_RADIUS.hero });
  }
  for (const minion of sim.minions) {
    if (minion.alive && minion.team === team) sources.push({ x: minion.pos.x, z: minion.pos.z, radius: VISION_RADIUS.minion });
  }
  for (const structure of sim.structures) {
    if (!structure.alive || structure.team !== team) continue;
    sources.push({
      x: structure.pos.x,
      z: structure.pos.z,
      radius: structure.kind === 'nexus' ? VISION_RADIUS.nexus : VISION_RADIUS.tower,
    });
  }
  return sources;
}

/** ¿El punto (x, z) cae dentro de algún foco de visión? */
export function isVisible(sources: readonly VisionSource[], x: number, z: number, padding = 0): boolean {
  for (const source of sources) {
    const radius = source.radius + padding;
    const dx = x - source.x;
    const dz = z - source.z;
    if (dx * dx + dz * dz <= radius * radius) return true;
  }
  return false;
}

/**
 * Margen (unidades) con el que el servidor sigue enviando una unidad enemiga ya
 * fuera de la visión: absorbe la interpolación del cliente (~100 ms) para que
 * nunca desaparezca antes de que su propia niebla la oculte.
 */
export const VISION_NET_PADDING = 3;

/** Eventos sólo visuales: se envían si ocurren a la vista del equipo o le afectan. */
const VISUAL_EVENTS = new Set<SimEvent['type']>([
  'attack',
  'hit',
  'projectileSpawn',
  'projectileHit',
  'projectileExplode',
  'minionDeath',
  'gold',
  'recall',
  'levelUp',
  'reflect',
  'respawn',
]);

/**
 * ¿Debe recibir el equipo este evento? Los globales (muertes de héroe,
 * estructuras, fin de partida, R de Link) siempre; los visuales sólo si son
 * visibles o involucran a una unidad propia.
 */
export function eventVisibleTo(
  event: SimEvent,
  team: Team,
  sources: readonly VisionSource[],
  ownIds: ReadonlySet<number>,
): boolean {
  if (!VISUAL_EVENTS.has(event.type)) return true;
  const involved = event as { heroId?: number; sourceId?: number; targetId?: number };
  if (involved.heroId !== undefined && ownIds.has(involved.heroId)) return true;
  if (involved.sourceId !== undefined && ownIds.has(involved.sourceId)) return true;
  if (involved.targetId !== undefined && ownIds.has(involved.targetId)) return true;
  // el oro y los niveles de los enemigos no son asunto tuyo
  if (event.type === 'gold' || event.type === 'levelUp') return false;
  if (event.type === 'minionDeath' && event.team === team) return true;
  const at = event as { x?: number; z?: number };
  if (at.x === undefined || at.z === undefined) return true;
  return isVisible(sources, at.x, at.z, VISION_NET_PADDING);
}
